import React, { useCallback, useEffect, useMemo } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';

import { closeModal, openModal } from 'interface/user/_redux/actions';
import {
  PageLayout,
  PageTabs,
  TranslatedNavLink,
  PageContentPreloader,
  Calendar,
  BasicModal,
  NoItemsPanel,
  PasswordChangeForm,
} from 'common/components';
import noOrdersImage from 'common/images/noOrders.svg';
import { withTheme } from 'common/styling/theme';
import { changeTradingAccountPassword } from '../_redux';
import { getTradingAccount, getTradingAccountOperations } from '../../_redux/actions';

import TradeOperationsTable from '../../_components/TradeOperationsTable';
import AccountInfoPanel from '../../_components/AccountInfoPanel';
import { AccountLeverageChangeForm } from '../_components';

import { staticStyles, getDynamicStyles } from './style';

const startDate = moment().startOf('day').unix();

const endDate = moment().endOf('day').unix();

const startCalendar = moment().startOf('day');

const endCalendar = moment().endOf('day');

const AccountPage = ({
  account,
  getTradingAccount,
  getOperations,
  operationsAreLoaded,
  operations,
  history,
  match,
  accountIsLoaded,
  modals,
  openModal,
  closeModal,
  addons,
  openOrders,
  closedOrders,
  delayedOrders,
  equityExist,
  redirect,
  theme,
}) => {
  const { accountId } = match.params;

  useEffect(() => {
    getTradingAccount(accountId);
  }, [getTradingAccount, accountId]);

  const isMt4Account = account.platformSlug === 'mt4';
  const isMt5Account = account.platformSlug === 'mt5';
  const isInvestMt5Account = account.platformSlug === 'investmt5';
  const isCfhAccount = account.platformSlug === 'cfh';
  const isYbAccount = account.platformSlug === 'yb';

  const OPERATION_STATUSES_BY_SLUG = {
    cfh: 'closed',
    yb: 'closed',
  };

  const getOperationStatus = useCallback(
    slug => match.params.operationStatus ?? OPERATION_STATUSES_BY_SLUG[slug],
    [OPERATION_STATUSES_BY_SLUG, match.params.operationStatus]
  );

  const convertedPendingStatus = isMt5Account ? 'deferred' : 'delayed';

  const operationStatus = useMemo(() => {
    const status = getOperationStatus(account.platformSlug);

    return status === 'pending' ? convertedPendingStatus : status;
  }, [getOperationStatus, account.platformSlug, convertedPendingStatus]);

  const memoizedOnChangeParams = useMemo(
    () => ({ id: Number(accountId), operationStatus, slug: account.platformSlug }),
    [accountId, operationStatus, account.platformSlug]
  );

  useEffect(() => {
    if (accountIsLoaded && Number(accountId) === account.id) {
      getOperations(accountId, operationStatus, startDate, endDate, account.platformSlug);
    }
  }, [getOperations, accountId, operationStatus, accountIsLoaded]);

  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const handleDeposit = () => {
    const redirectUrl = addons.includes('wallet')
      ? `/internalTransfer?tab=deposit&account=${accountId}`
      : `/deposit/${accountId}`;
    history.push(redirectUrl);
  };

  const handleWithdraw = () => {
    const redirectUrl = addons.includes('wallet')
      ? `/internalTransfer?tab=withdraw&account=${accountId}`
      : `/withdraw/${accountId}`;
    history.push(redirectUrl);
  };

  const handleChangeDate = (id, operationStatus, platformSlug, startDate, endDate) => {
    getOperations(id, operationStatus, startDate, endDate, platformSlug);
  };

  useEffect(() => {
    if (redirect) {
      window.location = '/';
    }
  }, [redirect]);

  const customPasswordLengthOptions = { min: 8, max: 15 };

  const shouldHideCalendar =
    ((isMt5Account || isInvestMt5Account) && !operationStatus) || operationStatus === 'delayed';

  return (
    <PageLayout
      captionSlug="clientAccount"
      extraCaptionText={account && account.login ? ` #${account.login}` : ''}
      showReload
      onReloadClick={() => getTradingAccount(accountId)}
      reloading={!accountIsLoaded}>
      <PageContentPreloader ready={accountIsLoaded}>
        {!!account.id && (
          <AccountInfoPanel
            {...account}
            onLeverageChangeClick={() => openModal('accountChangeLeverage')}
            onPasswordChangeClick={() => openModal('accountChangePassword')}
            onDepositClick={handleDeposit}
            onWithdrawClick={handleWithdraw}
            modals={modals}
            closeModal={closeModal}
            openModal={openModal}
            isBinary={account.platformTitle === 'Binary Options'}
          />
        )}

        {equityExist && (
          <>
            <div className="AccountPage__top-line">
              <PageTabs>
                {!(isCfhAccount || isYbAccount) && openOrders && (
                  <TranslatedNavLink id="justOpen" to={`/account/${accountId}`} />
                )}
                {closedOrders && (
                  <TranslatedNavLink
                    id={isCfhAccount || isYbAccount ? 'justDeals' : 'justClosed'}
                    to={isCfhAccount || isYbAccount ? `/account/${accountId}` : `/account/${accountId}/closed`}
                  />
                )}
                {delayedOrders && <TranslatedNavLink id="justPending" to={`/account/${accountId}/pending`} />}
              </PageTabs>
              <div className="AccountPage__date">
                {!shouldHideCalendar && (
                  <Calendar
                    daysLimit={operationStatus === 'closed' && isMt4Account ? 1 : 30}
                    onChangeFunc={handleChangeDate}
                    onChangeParams={memoizedOnChangeParams}
                    defaultStartDate={startCalendar}
                    defaultEndDate={endCalendar}
                    setCorrectLastDay
                  />
                )}
              </div>
            </div>
            <div className="AccountPage__table">
              <PageContentPreloader ready={operationsAreLoaded} type="smallLines">
                {operations?.items?.length ? (
                  <TradeOperationsTable
                    data={operations.items}
                    summary={operations.summary}
                    operationStatus={operationStatus}
                    isClient
                  />
                ) : (
                  <NoItemsPanel icon={noOrdersImage} captionSlug="yotHaveNoOrdersYet" />
                )}
              </PageContentPreloader>
            </div>
          </>
        )}
      </PageContentPreloader>
      <BasicModal
        captionId="authChangePassword"
        isOpen={modals.accountChangePassword}
        onRequestClose={() => closeModal('accountChangePassword')}>
        {modals.accountChangePassword && (
          <PasswordChangeForm
            id={accountId}
            platformSlug={account.platformSlug}
            changePasswordFunc={changeTradingAccountPassword}
            onSubmitSuccess={() => closeModal('accountChangePassword')}
            customLengthOptions={customPasswordLengthOptions}
          />
        )}
      </BasicModal>
      <BasicModal
        captionId="changeLeverage"
        isOpen={modals.accountChangeLeverage}
        onRequestClose={() => closeModal('accountChangeLeverage')}>
        {modals.accountChangeLeverage && (
          <AccountLeverageChangeForm
            accountId={accountId}
            options={account.availableLeverages}
            unlimitedLeverages={account.unlimitedLeverages}
            onSubmitSuccess={() => {
              closeModal('accountChangeLeverage');
              getTradingAccount(accountId);
            }}
            initialValues={{ leverage: account.leverage }}
          />
        )}
      </BasicModal>
      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </PageLayout>
  );
};

AccountPage.propTypes = {
  account: PropTypes.object.isRequired,
  operationsAreLoaded: PropTypes.bool.isRequired,
  operations: PropTypes.object,
  getTradingAccount: PropTypes.func.isRequired,
  getOperations: PropTypes.func.isRequired,
  openOrders: PropTypes.bool.isRequired,
  closedOrders: PropTypes.bool.isRequired,
  delayedOrders: PropTypes.bool.isRequired,
  accountIsLoaded: PropTypes.bool.isRequired,
  equityExist: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  modals: PropTypes.object.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  addons: PropTypes.array.isRequired,
  redirect: PropTypes.bool.isRequired,
  theme: PropTypes.object,
};

AccountPage.defaultProps = {
  operations: {},
  theme: {},
};

export default compose(
  withRouter,
  withTheme(),
  connect(
    state => {
      const { openOrders, closedOrders, delayedOrders } = state.interfaceConfig.equityTradingHistoryDisplay;

      const equityExist = [openOrders, closedOrders, delayedOrders].some(display => display);

      const account = state.client.common.currentAccount;
      const accountIsLoaded = state.client.common.currentAccountIsLoaded;

      const operations = state.client.common.currentAccountOperations;
      const operationsAreLoaded = state.client.common.currentAccountOperationsAreLoaded;

      return {
        account,
        accountIsLoaded,
        operations,
        operationsAreLoaded,
        openOrders,
        closedOrders,
        delayedOrders,
        equityExist,
        modals: state.user.modals,
        addons: state.interfaceConfig.addons,
        redirect: state.client.common.redirect,
      };
    },
    {
      getTradingAccount: id => getTradingAccount.request(id),
      getOperations: (id, operationStatus, startDate, endDate, accountPlatformSlug) =>
        getTradingAccountOperations.request(id, operationStatus, startDate, endDate, accountPlatformSlug),
      openModal: name => openModal(name),
      closeModal: name => closeModal(name),
    }
  )
)(AccountPage);
export { AccountPage };
